<template>
    <div class="import-students-page weeduc-admin-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="t.collaborators.importTitle"
                    :subtitle="t.collaborators.importSubtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <v-btn
                    variant="tonal"
                    color="grey-darken-2"
                    rounded="lg"
                    class="text-none"
                    prepend-icon="mdi-arrow-left"
                    @click="$router.push('/dashboard/corporativo/colaboradores')"
                >
                    Voltar
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div class="import-students-layout">
                <section class="import-students-panel import-students-panel--upload">
                    <h2 class="import-students-panel__title">Enviar arquivo</h2>
                    <p class="import-students-panel__lead">
                        {{ t.collaborators.importUploadLead }}
                    </p>

                    <label class="import-students-dropzone" for="collaboratorImportInput">
                        <input
                            id="collaboratorImportInput"
                            ref="fileInput"
                            type="file"
                            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            class="import-students-dropzone__input"
                            @change="onFileChange"
                        />
                        <v-icon size="36" color="primary">mdi-file-upload-outline</v-icon>
                        <strong v-if="!selectedFile">Clique para selecionar o CSV ou Excel</strong>
                        <strong v-else>{{ selectedFile.name }}</strong>
                        <span v-if="!selectedFile">
                            Formatos aceitos: .csv e .xlsx (máx. 5 MB)
                        </span>
                        <span v-else>
                            {{ formatBytes(selectedFile.size) }}
                        </span>
                    </label>

                    <div
                        v-if="selectedFile"
                        class="import-students-file-meta"
                    >
                        <v-btn
                            variant="text"
                            color="error"
                            class="text-none"
                            size="small"
                            prepend-icon="mdi-close"
                            :disabled="importing"
                            @click="clearFile"
                        >
                            Remover arquivo
                        </v-btn>
                    </div>

                    <div class="import-students-actions">
                        <v-btn
                            variant="flat"
                            color="primary"
                            class="text-none"
                            rounded="lg"
                            prepend-icon="mdi-check"
                            :loading="importing"
                            :disabled="!selectedFile || importing"
                            @click="importCollaborators"
                        >
                            {{ t.collaborators.importAction }}
                        </v-btn>
                    </div>

                    <div
                        v-if="mockResult"
                        class="import-students-result"
                        :class="`import-students-result--${mockResult.type}`"
                    >
                        {{ mockResult.message }}
                    </div>
                </section>

                <aside class="import-students-panel import-students-panel--template">
                    <h2 class="import-students-panel__title">Template CSV</h2>
                    <p class="import-students-panel__lead">
                        {{ t.collaborators.importTemplateLead }}
                    </p>

                    <p class="import-students-panel__section-label">
                        {{ t.collaborators.sectionPersonal }}
                    </p>
                    <ul class="import-students-columns">
                        <li
                            v-for="item in personalColumns"
                            :key="item.key"
                        >
                            <strong>{{ item.label }}</strong>
                            <span v-if="item.required"> *</span>
                            — <code>{{ item.key }}</code>
                        </li>
                    </ul>

                    <p class="import-students-panel__section-label mt-4">
                        {{ t.collaborators.sectionOrg }}
                    </p>
                    <ul class="import-students-columns">
                        <li
                            v-for="item in orgColumns"
                            :key="item.key"
                        >
                            <strong>{{ item.label }}</strong>
                            <span v-if="item.required"> *</span>
                            — <code>{{ item.key }}</code>
                            <small v-if="item.hint" class="d-block">{{ item.hint }}</small>
                        </li>
                    </ul>

                    <v-btn
                        block
                        variant="flat"
                        color="primary"
                        class="text-none mt-4"
                        rounded="lg"
                        prepend-icon="mdi-download"
                        @click="downloadTemplate"
                    >
                        Baixar template CSV
                    </v-btn>
                </aside>
            </div>
        </div>
    </div>
</template>

<script>
import TitlePage from '@/components/Weflix/titlePage.vue';
import { toast } from 'vue3-toastify';
import { mapState } from 'vuex';
import { corporateI18n } from '@/i18n/mixins';
import usersApi from '@/utils/corporate/users';
import {
    TEMPLATE_HEADERS_KEY,
    TEMPLATE_HEADERS_PT,
    buildCollaboratorImportCsv,
} from '@/utils/admin/collaboratorImportTemplate';

const REQUIRED_KEYS = new Set([
    'name',
    'email',
    'status',
    'socialNumber',
    'sector',
    'role',
]);

const ORG_KEYS = new Set(['sector', 'role', 'isResponsible']);

export default {
    name: 'ImportCollaborators',
    mixins: [corporateI18n],
    components: { TitlePage },
    data() {
        return {
            selectedFile: null,
            importing: false,
            mockResult: null,
        };
    },
    computed: {
        ...mapState(['token']),
        templateColumns() {
            return TEMPLATE_HEADERS_PT.map((label, index) => {
                const key = TEMPLATE_HEADERS_KEY[index];
                return {
                    label,
                    key,
                    required: REQUIRED_KEYS.has(key),
                    hint: key === 'isResponsible'
                        ? 'Use sim ou nao'
                        : key === 'status'
                            ? 'active ou inactive'
                            : key === 'sector' || key === 'role'
                                ? 'Nome exatamente como cadastrado na estrutura'
                                : '',
                };
            });
        },
        personalColumns() {
            return this.templateColumns.filter((col) => !ORG_KEYS.has(col.key));
        },
        orgColumns() {
            return this.templateColumns.filter((col) => ORG_KEYS.has(col.key));
        },
    },
    methods: {
        onFileChange(event) {
            const file = event.target.files?.[0] || null;
            this.mockResult = null;

            if (!file) {
                this.selectedFile = null;
                return;
            }

            if (!this.isAcceptedImportFile(file)) {
                toast.error('Selecione um arquivo CSV ou Excel (.xlsx) válido.');
                this.clearFile();
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                toast.error('O arquivo deve ter no máximo 5 MB.');
                this.clearFile();
                return;
            }

            this.selectedFile = file;
        },
        clearFile() {
            this.selectedFile = null;
            this.mockResult = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },
        isAcceptedImportFile(file) {
            const name = (file.name || '').toLowerCase();
            const type = (file.type || '').toLowerCase();
            return name.endsWith('.csv')
                || name.endsWith('.xlsx')
                || type === 'text/csv'
                || type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        },
        formatBytes(bytes) {
            if (!bytes && bytes !== 0) return '';
            if (bytes < 1024) return `${bytes} B`;
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        },
        downloadTemplate() {
            const blob = new Blob([buildCollaboratorImportCsv()], {
                type: 'text/csv;charset=utf-8;',
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'template-importacao-colaboradores.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('Template CSV baixado com todas as colunas.');
        },
        async importCollaborators() {
            if (!this.selectedFile) return;

            this.importing = true;
            this.mockResult = null;

            try {
                const result = await usersApi.importCollaborators(this.token, this.selectedFile);
                const failed = Number(result.failed || 0);
                const imported = Number(result.imported || 0);
                const total = Number(result.total || 0);
                const failureLines = (result.failures || [])
                    .map((row) => `Linha ${row.line}: ${row.email || row.name || '-'} — ${row.message}`)
                    .join(' ');
                this.mockResult = {
                    type: imported > 0 ? 'success' : 'error',
                    message: `Importação concluída: ${imported} cadastrado(s), ${failed} falha(s) de ${total}. ${failureLines}`.trim(),
                };
                if (imported > 0) {
                    toast.success(`${imported} colaborador(es) cadastrado(s).`);
                } else {
                    toast.error('Não foi possível cadastrar os colaboradores.');
                }
            } catch (error) {
                console.error('[import-collaborators] falha', error);
                this.mockResult = {
                    type: 'error',
                    message: error.response?.data?.message || error.message || 'Não foi possível ler o arquivo. Tente novamente.',
                };
                toast.error('Falha ao processar o arquivo.');
            } finally {
                this.importing = false;
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/dashboard-weflix-shell.css';
@import '@/css/weeduc/admin/corporate.css';

.import-students-layout {
    display: grid;
    grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
    gap: 1rem;
    align-items: start;
}

.import-students-panel {
    background: #fff;
    border: 1px solid #eef0f4;
    border-radius: 14px;
    padding: 1.25rem 1.35rem 1.4rem;
}

.import-students-panel__title {
    margin: 0 0 0.35rem;
    color: #1a1d26;
    font-size: 1.05rem;
    font-weight: 700;
}

.import-students-panel__lead {
    margin: 0 0 1.1rem;
    color: #5c6370;
    font-size: 0.875rem;
    line-height: 1.45;
}

.import-students-panel__section-label {
    margin: 0 0 0.35rem;
    color: #160266;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.import-students-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    min-height: 180px;
    padding: 1.5rem 1rem;
    border: 1.5px dashed #c5c9d4;
    border-radius: 12px;
    background: #fafbfc;
    cursor: pointer;
    text-align: center;
    transition: border-color 0.15s ease, background-color 0.15s ease;
}

.import-students-dropzone:hover {
    border-color: #160266;
    background: #f6f5fd;
}

.import-students-dropzone__input {
    display: none;
}

.import-students-dropzone strong {
    color: #1a1d26;
    font-size: 0.95rem;
}

.import-students-dropzone span {
    color: #6b7280;
    font-size: 0.8125rem;
}

.import-students-file-meta {
    margin-top: 0.5rem;
}

.import-students-actions {
    margin-top: 1.1rem;
}

.import-students-result {
    margin-top: 1rem;
    padding: 0.85rem 1rem;
    border-radius: 10px;
    font-size: 0.875rem;
    line-height: 1.45;
}

.import-students-result--success {
    background: #eaf7ef;
    color: #146c2e;
}

.import-students-result--error {
    background: #fdeeee;
    color: #9b1c1c;
}

.import-students-columns {
    margin: 0;
    padding-left: 1.1rem;
    color: #4b5563;
    font-size: 0.85rem;
    line-height: 1.7;
}

.import-students-columns code {
    font-size: 0.8rem;
    color: #160266;
}

.import-students-columns small {
    color: #7a8190;
    font-size: 0.75rem;
    line-height: 1.35;
}

@media (max-width: 959px) {
    .import-students-layout {
        grid-template-columns: 1fr;
    }
}

.dashboard.dark .import-students-panel {
    background: #1e2128;
    border-color: #353945;
}

.dashboard.dark .import-students-panel__title {
    color: #f1f3f7;
}

.dashboard.dark .import-students-panel__lead,
.dashboard.dark .import-students-columns,
.dashboard.dark .import-students-dropzone span,
.dashboard.dark .import-students-columns small {
    color: #9ca3b4;
}

.dashboard.dark .import-students-dropzone {
    background: #252730;
    border-color: #454b5a;
}

.dashboard.dark .import-students-dropzone:hover {
    border-color: #c4b5fd;
    background: #2a2e3a;
}

.dashboard.dark .import-students-dropzone strong {
    color: #f1f3f7;
}

.dashboard.dark .import-students-panel a,
.dashboard.dark .import-students-columns a {
    color: #c4b5fd;
}

.dashboard.dark .import-students-columns code {
    color: #c4b5fd;
}

.dashboard.dark .import-students-panel__title {
    color: #f1f3f7;
}

.dashboard.dark .import-students-panel__section-label {
    color: #c4b5fd;
}

.dashboard.dark .import-students-result--success {
    background: rgba(46, 125, 50, 0.18);
    color: #86efac;
}

.dashboard.dark .import-students-result--error {
    background: rgba(155, 28, 28, 0.2);
    color: #fca5a5;
}
</style>
